import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { warrantyApi, uploadsApi } from '../../services/api';

const componentOptions = [
  { value: 'motor', label: 'Motor' },
  { value: 'fabric', label: 'Fabric' },
  { value: 'track', label: 'Track' },
  { value: 'housing', label: 'Housing' },
  { value: 'remote', label: 'Remote' },
  { value: 'other', label: 'Other' },
];

const issueTypeOptions = [
  { value: 'manufacturing_defect', label: 'Manufacturing Defect' },
  { value: 'shipping_damage', label: 'Shipping Damage' },
  { value: 'motor_failure', label: 'Motor Failure' },
  { value: 'fabric_tear', label: 'Fabric Tear' },
  { value: 'track_binding', label: 'Track Binding' },
  { value: 'weather_damage', label: 'Weather Damage' },
  { value: 'other', label: 'Other' },
];

export const SubmitRmaPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ rmaNumber: string } | null>(null);

  // Lookup
  const [lookupType, setLookupType] = useState<'order' | 'warranty'>('order');
  const [lookupValue, setLookupValue] = useState('');
  const [lookupResult, setLookupResult] = useState<any>(null);

  // Form data
  const [component, setComponent] = useState('');
  const [issueType, setIssueType] = useState('');
  const [description, setDescription] = useState('');

  // Photo uploads
  const [photoUrls, setPhotoUrls] = useState<string[]>([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const performLookup = async () => {
    if (!lookupValue.trim()) return;

    setLookupLoading(true);
    setError(null);

    try {
      if (lookupType === 'order') {
        const data = await warrantyApi.lookupOrder(lookupValue.trim());
        setLookupResult({ type: 'order', data });
      } else {
        // For warranty lookup, we'd need to search by registration ID or similar
        const response = await warrantyApi.listRegistrations({ search: lookupValue.trim(), pageSize: 1 });
        if (response.data && response.data.length > 0) {
          setLookupResult({ type: 'warranty', data: response.data[0] });
        } else {
          throw new Error('Warranty registration not found');
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Not found');
      setLookupResult(null);
    } finally {
      setLookupLoading(false);
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingPhoto(true);
    setError(null);

    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        const result = await uploadsApi.uploadRmaPhoto(file);
        return result.url;
      });

      const urls = await Promise.all(uploadPromises);
      setPhotoUrls((prev) => [...prev, ...urls].slice(0, 6)); // Max 6 photos
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to upload photos. Please try again.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const removePhoto = (index: number) => {
    setPhotoUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validation
    if (photoUrls.length < 2) {
      setError('Please upload at least 2 photos showing the issue');
      setLoading(false);
      return;
    }

    if (!component || !issueType || !description.trim()) {
      setError('Please fill in all required fields');
      setLoading(false);
      return;
    }

    try {
      const rmaData: any = {
        component,
        issueType,
        description: description.trim(),
        photoUrls,
      };

      if (lookupResult?.type === 'order') {
        rmaData.orderId = lookupResult.data.id;
        rmaData.orderNumber = lookupResult.data.orderNumber;
      } else if (lookupResult?.type === 'warranty') {
        rmaData.warrantyRegistrationId = lookupResult.data.id;
        rmaData.orderNumber = lookupResult.data.orderNumber;
      }

      const result = await warrantyApi.createRma(rmaData);
      setSuccess({ rmaNumber: result.rmaNumber });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to submit RMA request');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="card p-8 text-center">
          <div className="text-green-500 text-6xl mb-4">&#10003;</div>
          <h2 className="text-2xl font-bold text-white mb-2">RMA Submitted!</h2>
          <p className="text-gray-400 mb-2">Your RMA request has been submitted for review.</p>
          <p className="text-keder-yellow text-xl font-bold mb-6">RMA #: {success.rmaNumber}</p>
          <p className="text-gray-400 text-sm mb-6">
            You will receive an email notification once your request has been reviewed.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => {
                setSuccess(null);
                setLookupResult(null);
                setLookupValue('');
                setPhotoUrls([]);
                setComponent('');
                setIssueType('');
                setDescription('');
              }}
              className="btn-primary w-full"
            >
              Submit Another RMA
            </button>
            <button
              onClick={() => navigate('/warranty/my-rmas')}
              className="btn-secondary w-full"
            >
              View My RMAs
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Submit RMA Request</h1>
        <p className="text-gray-400 mt-1">
          Submit a Return Merchandise Authorization request for warranty claims
        </p>
      </div>

      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-300 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Order/Warranty Lookup */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Find Order or Warranty</h2>
          <div className="flex flex-col sm:flex-row gap-3">
            <select
              value={lookupType}
              onChange={(e) => setLookupType(e.target.value as 'order' | 'warranty')}
              className="select-field"
            >
              <option value="order">Order Number</option>
              <option value="warranty">Warranty Registration</option>
            </select>
            <input
              type="text"
              placeholder={lookupType === 'order' ? 'Enter order number...' : 'Enter warranty registration...'}
              value={lookupValue}
              onChange={(e) => setLookupValue(e.target.value)}
              className="input-field flex-1"
            />
            <button
              type="button"
              onClick={performLookup}
              disabled={lookupLoading || !lookupValue.trim()}
              className="btn-secondary"
            >
              {lookupLoading ? 'Searching...' : 'Look Up'}
            </button>
          </div>

          {lookupResult && (
            <div className="mt-4 p-4 bg-green-900/30 border border-green-600 rounded-lg">
              <div className="flex items-center gap-2 text-green-400 mb-2">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="font-medium">Found</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-gray-400">Order #:</span>
                  <p className="text-white font-medium">
                    {lookupResult.data.orderNumber}
                  </p>
                </div>
                {lookupResult.type === 'order' && (
                  <>
                    <div>
                      <span className="text-gray-400">Status:</span>
                      <p className="text-white">{lookupResult.data.status}</p>
                    </div>
                    <div>
                      <span className="text-gray-400">Units:</span>
                      <p className="text-white">{lookupResult.data.itemCount}</p>
                    </div>
                  </>
                )}
                {lookupResult.type === 'warranty' && (
                  <>
                    <div>
                      <span className="text-gray-400">Customer:</span>
                      <p className="text-white">{lookupResult.data.customerName}</p>
                    </div>
                    <div>
                      <span className="text-gray-400">Install Date:</span>
                      <p className="text-white">
                        {new Date(lookupResult.data.installDate).toLocaleDateString()}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Issue Details */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Issue Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">
                Component *
              </label>
              <select
                required
                value={component}
                onChange={(e) => setComponent(e.target.value)}
                className="select-field w-full"
              >
                <option value="">Select component...</option>
                {componentOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">
                Issue Type *
              </label>
              <select
                required
                value={issueType}
                onChange={(e) => setIssueType(e.target.value)}
                className="select-field w-full"
              >
                <option value="">Select issue type...</option>
                {issueTypeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">
              Description *
            </label>
            <textarea
              required
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="input-field w-full"
              placeholder="Please describe the issue in detail..."
            />
          </div>
        </div>

        {/* Photo Uploads */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-white mb-2">Photos of Issue</h2>
          <p className="text-gray-400 text-sm mb-4">
            Upload 2-6 photos showing: close-up of damage, full unit view, serial number tag
          </p>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
            {photoUrls.map((url, index) => (
              <div key={index} className="relative group">
                <img
                  src={url}
                  alt={`Photo ${index + 1}`}
                  className="w-full h-32 object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => removePhoto(index)}
                  className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}

            {photoUrls.length < 6 && (
              <label className="border-2 border-dashed border-keder-gray rounded-lg h-32 flex flex-col items-center justify-center cursor-pointer hover:border-keder-yellow transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoUpload}
                  className="hidden"
                  disabled={uploadingPhoto}
                />
                {uploadingPhoto ? (
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-keder-yellow" />
                ) : (
                  <>
                    <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <span className="text-gray-400 text-sm mt-1">Add Photo</span>
                  </>
                )}
              </label>
            )}
          </div>

          {photoUrls.length < 2 && (
            <p className="text-amber-400 text-sm">* Minimum 2 photos required</p>
          )}
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-3">
          <button type="button" onClick={() => navigate(-1)} className="btn-secondary">
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || photoUrls.length < 2 || !component || !issueType || !description.trim()}
            className="btn-primary"
          >
            {loading ? 'Submitting...' : 'Submit RMA Request'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SubmitRmaPage;
